import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from "@/hooks/use-translation"
import { getAvailableGames, getLocalizedGameId } from '@/lib/games-data'
import { useFavorites } from "@/context/favorites-context"
import { Star } from 'lucide-react'

interface GameShortcutsProps {
  currentGame: string
}

export function GameShortcuts({ currentGame }: GameShortcutsProps) {
  const { t, navigate: localizedNavigate, language } = useTranslation()
  const { isFavorite } = useFavorites()

  const availableGames = getAvailableGames(language)
  const otherGames = availableGames.filter(game => game.id !== currentGame)
  
  // Separar juegos en favoritos y otros
  const favoriteGames = otherGames.filter(game => isFavorite(game.id))
  const nonFavoriteGames = otherGames.filter(game => !isFavorite(game.id))

  const handleGameClick = (gameId: string) => {
    const localizedId = getLocalizedGameId(gameId, language)
    localizedNavigate(`/game/${localizedId}`)
  }


  return (
    <Card className="mt-6 w-full max-w-xs mx-auto lg:max-w-none lg:mx-0 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm text-center lg:text-left flex items-center gap-2">
          <span>{t("shortcuts.title")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Favorite Games Section */}
          {favoriteGames.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                {t("home.favoriteGames")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {favoriteGames.map((game) => {
                  const IconComponent = game.icon
                  return (
                    <Button
                      key={game.id}
                      variant="outline"
                      size="sm"
                      className="h-auto flex items-center gap-2 p-2.5 hover:bg-primary/10 hover:border-primary transition-colors group"
                      onClick={() => handleGameClick(game.id)}
                    >
                      <IconComponent className="h-4 w-4 lg:h-5 lg:w-5 text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-medium text-left flex-1">{t(`games.${game.id}.name`)}</span>
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Other Games Section */}
          {nonFavoriteGames.length > 0 && (
            <div className="space-y-2">
              {favoriteGames.length > 0 && (
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("home.otherGames")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {nonFavoriteGames.map((game) => {
                  const IconComponent = game.icon
                  return (
                    <Button
                      key={game.id}
                      variant="outline"
                      size="sm"
                      className="h-auto flex items-center gap-2 p-2.5 hover:bg-primary/10 hover:border-primary transition-colors group"
                      onClick={() => handleGameClick(game.id)}
                    >
                      <IconComponent className="h-4 w-4 lg:h-5 lg:w-5 text-muted-foreground group-hover:text-primary flex-shrink-0 group-hover:scale-110 transition-all" />
                      <span className="text-xs font-medium text-left flex-1">{t(`games.${game.id}.name`)}</span>
                    </Button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
} 